/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */


/* -------------------------------------------------------------------------- */
/*  Warranty picker (cart page)                                               */
/* -------------------------------------------------------------------------- */
(function () {
  if (window.customElements.get('warranty-picker')) return;

  let cachedPlans = null;

  function loadPlans() {
    if (cachedPlans) return cachedPlans;
    const node = document.querySelector('script[data-warranty-plans]');
    if (!node) return (cachedPlans = []);
    try {
      cachedPlans = JSON.parse(node.textContent);
    } catch (e) {
      console.warn('[warranty] Failed to parse plans JSON', e);
      cachedPlans = [];
    }
    return cachedPlans;
  }

  // Classify a variant SKU into { kind, min, max, size }.
  //   SKU examples: "0-799_Furniture", "700-1499_Mattress", "Adj-Base-Q"
  function classify(sku) {
    if (!sku) return null;
    const trimmed = String(sku).trim();
    const rangeMatch = trimmed.match(/^(\d+)-(\d+)_(Furniture|Mattress)$/i);
    if (rangeMatch) {
      return {
        kind: rangeMatch[3].toLowerCase(),
        min: parseInt(rangeMatch[1], 10),
        max: parseInt(rangeMatch[2], 10)
      };
    }
    const adjMatch = trimmed.match(/^Adj-Base-([A-Z]+)$/i);
    if (adjMatch) {
      return { kind: 'adj-base', size: adjMatch[1].toLowerCase() };
    }
    return null;
  }

  function resolveVariant(warrantyType, priceCents) {
    const plans = loadPlans();
    if (!plans.length) return null;

    if (warrantyType.startsWith('adj-base-')) {
      const size = warrantyType.slice('adj-base-'.length);
      return plans.find(p => {
        if (!p.available) return false;
        const c = classify(p.sku);
        return c && c.kind === 'adj-base' && c.size === size;
      }) || null;
    }

    // Floor the price to the whole dollar so tiers behave as half-open ranges:
    //   "0-799"  → $0.00 ≤ price < $800.00  (i.e. $799.98 still falls in this tier)
    //   "800-1199" → $800.00 ≤ price < $1200.00
    const priceDollarsFloor = Math.floor(priceCents / 100);
    return plans.find(p => {
      if (!p.available) return false;
      const c = classify(p.sku);
      return c && c.kind === warrantyType && priceDollarsFloor >= c.min && priceDollarsFloor <= c.max;
    }) || null;
  }

  function formatMoney(cents) {
    // Delegate to Shopify money format if theme exposes it; otherwise plain USD.
    if (window.theme && window.theme.moneyFormat && window.Currency && window.Currency.formatMoney) {
      try { return window.Currency.formatMoney(cents, window.theme.moneyFormat); } catch (e) { /* fall through */ }
    }
    return '$' + (cents / 100).toFixed(2);
  }

  class WarrantyPicker extends HTMLElement {
    connectedCallback() {
      if (this._rendered) return;
      this._rendered = true;
      this.render();
    }

    render() {
      const warrantyType = this.dataset.warrantyType;
      const priceCents = parseInt(this.dataset.priceCents, 10) || 0;
      const variant = resolveVariant(warrantyType, priceCents);

      if (!variant) {
        // No tier matches — silent no-op is fine; the merchant may not have configured a matching range.
        this.hidden = true;
        return;
      }

      const heading = this.dataset.heading || 'Add protection plan';
      const buttonLabel = this.dataset.buttonLabel || 'Add protection';
      const lineKey = this.dataset.lineKey || '';
      const learnMoreUrl = this.dataset.learnMoreUrl || '';

      const learnMoreHtml = learnMoreUrl
        ? '<a href="' + escapeHtml(learnMoreUrl) + '" class="warranty-picker__learn-more link" data-warranty-learn-more>Learn more</a>'
        : '';

      this.innerHTML = [
        '<div class="warranty-picker__inner">',
          '<div class="warranty-picker__meta">',
            '<span class="warranty-picker__icon" aria-hidden="true">',
              '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2 4 5v7c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V5l-8-3z"/><path d="m9 12 2 2 4-4"/></svg>',
            '</span>',
            '<div class="warranty-picker__text">',
              '<span class="warranty-picker__heading">', escapeHtml(heading), '</span>',
              '<span class="warranty-picker__variant">', escapeHtml(variant.title), ' — ', formatMoney(variant.price), '</span>',
              learnMoreHtml,
            '</div>',
          '</div>',
          '<button type="button" class="button button--secondary warranty-picker__button" data-variant-id="', variant.id, '" data-line-key="', escapeHtml(lineKey), '">',
            escapeHtml(buttonLabel),
          '</button>',
        '</div>'
      ].join('');

      this.querySelector('button').addEventListener('click', this.onClick.bind(this));
      const lm = this.querySelector('[data-warranty-learn-more]');
      if (lm) {
        lm.addEventListener('click', function (e) {
          e.preventDefault();
          openLearnMoreDrawer(learnMoreUrl);
        });
      }
    }

    onClick(event) {
      const btn = event.currentTarget;
      if (btn.disabled) return;
      btn.disabled = true;

      const variantId = btn.dataset.variantId;
      const lineKey = btn.dataset.lineKey;
      const lineTitle = this.dataset.lineTitle || '';
      const warrantyType = this.dataset.warrantyType || '';
      const lineQty = parseInt(this.dataset.lineQuantity, 10) || 1;

      // Adjustable-base warranties have no price tiering, so we add one warranty per unit.
      // Furniture/mattress use tier-by-line-total, so quantity is always 1.
      const isAdjBase = warrantyType.indexOf('adj-base-') === 0;
      const quantityToAdd = isAdjBase ? lineQty : 1;

      document.dispatchEvent(new CustomEvent('theme:loading:start'));

      const body = new FormData();
      body.append('id', variantId);
      body.append('quantity', String(quantityToAdd));
      body.append('properties[_protects_line]', lineKey);
      if (lineTitle) body.append('properties[_protects_title]', lineTitle);

      fetch((window.routes && window.routes.cartAddUrl ? window.routes.cartAddUrl : '/cart/add') + '.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
        body: body
      }).then(response => {
        if (!response.ok) {
          return response.json().then(err => { throw new Error(err.description || 'Add to cart failed'); });
        }
        const onCartPage = window.theme && (window.theme.pageType === 'cart' || window.theme.cartType === 'page');
        if (onCartPage) {
          // Reload so the cart-page Liquid re-renders line badges + hides the picker.
          window.location.href = (window.routes && window.routes.cartUrl) ? window.routes.cartUrl : '/cart';
          return;
        }
        // Drawer / message contexts: dispatch product:added so the theme re-renders the mini-cart.
        document.documentElement.dispatchEvent(new CustomEvent('product:added', {
          bubbles: true,
          detail: { quantity: 1, productAdded: true, button: btn }
        }));
      }).catch(err => {
        console.error('[warranty] add-to-cart failed', err);
        btn.disabled = false;
        document.dispatchEvent(new CustomEvent('theme:loading:end'));
        alert(err.message || 'Could not add the protection plan.');
      });
    }
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* ---- Learn more drawer ---- */
  let learnMoreEl = null;
  const contentCache = {};

  function buildLearnMoreDrawer() {
    const wrapper = document.createElement('div');
    wrapper.className = 'warranty-drawer';
    wrapper.setAttribute('aria-hidden', 'true');
    wrapper.innerHTML = [
      '<div class="warranty-drawer__backdrop" data-warranty-drawer-close></div>',
      '<aside class="warranty-drawer__panel" role="dialog" aria-modal="true" aria-labelledby="warranty-drawer-title">',
        '<header class="warranty-drawer__header">',
          '<h2 id="warranty-drawer-title" class="warranty-drawer__title">Protection plan</h2>',
          '<button type="button" class="warranty-drawer__close" data-warranty-drawer-close aria-label="Close">',
            '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M6 18L18 6"/></svg>',
          '</button>',
        '</header>',
        '<div class="warranty-drawer__body">',
          '<div class="warranty-drawer__loading">Loading…</div>',
          '<div class="warranty-drawer__content rte" hidden></div>',
        '</div>',
        '<footer class="warranty-drawer__footer">',
          '<a href="#" class="button button--primary warranty-drawer__cta" data-warranty-drawer-cta>View protection</a>',
        '</footer>',
      '</aside>'
    ].join('');
    document.body.appendChild(wrapper);

    wrapper.addEventListener('click', function (e) {
      if (e.target.closest('[data-warranty-drawer-close]')) {
        closeLearnMoreDrawer();
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && wrapper.classList.contains('is-open')) {
        closeLearnMoreDrawer();
      }
    });
    return wrapper;
  }

  function openLearnMoreDrawer(url) {
    if (!url) return;

    // Close the mini-cart if it's open, so drawers don't stack
    const openMiniCart = document.querySelector('.mini-cart[aria-hidden="false"]');
    if (openMiniCart) {
      const toggle = document.querySelector('[data-action="toggle-mini-cart"]');
      if (toggle) toggle.click();
    }

    if (!learnMoreEl) learnMoreEl = buildLearnMoreDrawer();

    const contentEl = learnMoreEl.querySelector('.warranty-drawer__content');
    const loadingEl = learnMoreEl.querySelector('.warranty-drawer__loading');
    const ctaEl = learnMoreEl.querySelector('[data-warranty-drawer-cta]');

    ctaEl.href = url;

    learnMoreEl.classList.add('is-open');
    learnMoreEl.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('warranty-drawer-open');

    if (contentCache[url]) {
      loadingEl.hidden = true;
      contentEl.hidden = false;
      contentEl.innerHTML = contentCache[url];
      return;
    }

    loadingEl.hidden = false;
    contentEl.hidden = true;
    contentEl.innerHTML = '';

    fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then(r => r.text())
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const page = doc.querySelector('.page__content')
          || doc.querySelector('main .rte')
          || doc.querySelector('main');
        const titleEl = doc.querySelector('.page__title') || doc.querySelector('h1');
        if (titleEl) {
          const titleTarget = learnMoreEl.querySelector('.warranty-drawer__title');
          if (titleTarget) titleTarget.textContent = titleEl.textContent.trim();
        }
        const inner = page ? page.innerHTML : '<p>Content could not be loaded.</p>';
        contentCache[url] = inner;
        loadingEl.hidden = true;
        contentEl.hidden = false;
        contentEl.innerHTML = inner;
      })
      .catch(err => {
        console.error('[warranty] learn-more fetch failed', err);
        loadingEl.textContent = 'Content could not be loaded.';
      });
  }

  function closeLearnMoreDrawer() {
    if (!learnMoreEl) return;
    learnMoreEl.classList.remove('is-open');
    learnMoreEl.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('warranty-drawer-open');
  }

  window.customElements.define('warranty-picker', WarrantyPicker);

  /* ---- Warranty attachments map (parentKey -> warrantyKey) ---- */
  function loadAttachments() {
    const node = document.querySelector('script[data-warranty-attachments]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent) || {};
    } catch (e) {
      console.warn('[warranty] Failed to parse attachments JSON', e);
      return {};
    }
  }

  function applyCartUpdates(updates) {
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json'
      },
      body: JSON.stringify({ updates: updates })
    }).then(r => {
      if (!r.ok) throw new Error('cart update failed');
      return r.json();
    });
  }

  function refreshCartView() {
    if (window.theme && (window.theme.pageType === 'cart' || window.theme.cartType === 'page')) {
      window.location.reload();
    } else {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }
  }

  /* ---- Quantity sync: swap warranty variant when parent line total crosses a tier ---- */
  function interceptQuantityChange(event, parentKey, newQty) {
    const attachments = loadAttachments();
    const warrantyKey = attachments[parentKey];
    if (!warrantyKey) return false; // no warranty attached, let theme handle normally

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(r => r.json())
      .then(cart => {
        const parent = (cart.items || []).find(i => i.key === parentKey);
        const warranty = (cart.items || []).find(i => i.key === warrantyKey);

        // Fallback: state drifted — just apply parent qty change and refresh
        if (!parent || !warranty) {
          return applyCartUpdates({ [parentKey]: newQty }).then(refreshCartView);
        }

        const classified = classify(warranty.sku);
        const isAdjBase = classified && classified.kind === 'adj-base';
        const priceTiered = classified && (classified.kind === 'furniture' || classified.kind === 'mattress');

        // Adjustable base: variant is fixed by size, warranty qty tracks parent qty 1:1.
        if (isAdjBase) {
          const updates = { [parentKey]: newQty };
          if (warranty.quantity !== newQty) updates[warrantyKey] = newQty;
          return applyCartUpdates(updates).then(refreshCartView);
        }

        // Unknown warranty kind — sync parent qty, leave warranty at qty 1 as a safe default.
        if (!priceTiered) {
          const updates = { [parentKey]: newQty };
          if (warranty.quantity !== 1) updates[warrantyKey] = 1;
          return applyCartUpdates(updates).then(refreshCartView);
        }

        const newLineTotalCents = parent.original_price * newQty;
        const newVariant = resolveVariant(classified.kind, newLineTotalCents);
        const updates = { [parentKey]: newQty };

        if (!newVariant) {
          // Line total outside all tiers — drop the warranty rather than leave a mismatched tier
          updates[warrantyKey] = 0;
          return applyCartUpdates(updates).then(refreshCartView);
        }

        if (newVariant.id === warranty.variant_id) {
          // Same tier — only make sure warranty qty is 1
          if (warranty.quantity !== 1) updates[warrantyKey] = 1;
          return applyCartUpdates(updates).then(refreshCartView);
        }

        // Different tier — remove old warranty + apply new parent qty, then add the new warranty
        updates[warrantyKey] = 0;
        return applyCartUpdates(updates).then(() => {
          const fd = new FormData();
          fd.append('id', String(newVariant.id));
          fd.append('quantity', '1');
          fd.append('properties[_protects_line]', parentKey);
          if (parent.product_title) fd.append('properties[_protects_title]', parent.product_title);
          return fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
            body: fd
          });
        }).then(refreshCartView);
      })
      .catch(err => {
        console.error('[warranty] qty sync failed', err);
        document.dispatchEvent(new CustomEvent('theme:loading:end'));
      });
    return true;
  }

  // +/- button clicks — data-quantity holds the target quantity
  document.addEventListener('click', function (event) {
    const btn = event.target.closest(
      '[data-action="increase-quantity"][data-line-key], [data-action="decrease-quantity"][data-line-key]'
    );
    if (!btn) return;

    const qtyAttr = btn.getAttribute('data-quantity');
    if (qtyAttr === '0' || qtyAttr === null) return; // Remove link is handled by the cascading-remove listener

    const newQty = parseInt(qtyAttr, 10);
    if (isNaN(newQty)) return;

    interceptQuantityChange(event, btn.dataset.lineKey, newQty);
  }, true);

  // Direct input edits — fires on blur / Enter
  document.addEventListener('change', function (event) {
    const input = event.target.closest('.quantity-selector__value[data-line-key]');
    if (!input) return;

    const newQty = parseInt(input.value, 10);
    if (isNaN(newQty) || newQty < 0) return;
    if (newQty === 0) return; // treat as remove — handled elsewhere

    interceptQuantityChange(event, input.dataset.lineKey, newQty);
  }, true);

  /* ---- Cascading removal: when a protected line is removed, remove its warranty too ---- */
  document.addEventListener('click', function (event) {
    const link = event.target.closest(
      '[data-action="decrease-quantity"][data-quantity="0"][data-warranty-attached="true"]'
    );
    if (!link) return;

    const parentKey = link.dataset.lineKey;
    if (!parentKey) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(r => r.json())
      .then(cart => {
        const warrantyKeys = (cart.items || [])
          .filter(item => item.properties && item.properties._protects_line === parentKey)
          .map(item => item.key);

        const updates = {};
        updates[parentKey] = 0;
        warrantyKeys.forEach(k => { updates[k] = 0; });

        return fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json'
          },
          body: JSON.stringify({ updates: updates })
        });
      })
      .then(response => {
        if (!response.ok) throw new Error('cart update failed');
        if (window.theme && (window.theme.pageType === 'cart' || window.theme.cartType === 'page')) {
          window.location.reload();
        } else {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
      })
      .catch(err => {
        console.error('[warranty] cascade remove failed', err);
        document.dispatchEvent(new CustomEvent('theme:loading:end'));
      });
  }, true);
})();

/* -------------------------------------------------------------------------- */
/*  Terms & conditions gate (cart page + mini-cart)                            */
/* -------------------------------------------------------------------------- */
(function () {
  const CHECKBOX = '[data-terms-checkbox]';
  const BUTTON = '[data-terms-gated]';
  const ERROR = '[data-terms-error]';

  // Both the cart page and the mini-cart keep the checkbox and the checkout
  // button inside the same cart form, so the form is the scope of a gate.
  function scopeFor(element) {
    return element.closest('form') || document;
  }

  function toggleError(scope, visible) {
    const error = scope.querySelector(ERROR);
    if (error) error.hidden = !visible;
  }

  function sync(scope) {
    const checkbox = scope.querySelector(CHECKBOX);
    if (!checkbox) return;

    const agreed = checkbox.checked;

    scope.querySelectorAll(BUTTON).forEach(button => {
      button.disabled = !agreed;
    });

    if (agreed) {
      toggleError(scope, false);
    }
  }

  document.addEventListener('change', function (event) {
    const checkbox = event.target.closest && event.target.closest(CHECKBOX);
    if (!checkbox) return;
    sync(scopeFor(checkbox));
  });

  // Second line of defence: the checkout button ships disabled from Liquid, but
  // the form can still be submitted by pressing Enter in the order note, or by
  // anything that re-enables the button outside of our control.
  document.addEventListener('submit', function (event) {
    const form = event.target;
    if (!form || !form.querySelector) return;

    const checkbox = form.querySelector(CHECKBOX);
    if (!checkbox || checkbox.checked) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    toggleError(form, true);
    checkbox.focus();
  }, true);

  // The mini-cart is re-rendered whenever the cart changes, and a fresh render
  // always starts locked (both the checkbox and the disabled button come from
  // Liquid). This only re-locks what a browser restored from its back/forward
  // cache, where the checkbox state can survive but the button state may not.
  function syncAll() {
    document.querySelectorAll(CHECKBOX).forEach(checkbox => sync(scopeFor(checkbox)));
  }

  document.addEventListener('DOMContentLoaded', syncAll);
  window.addEventListener('pageshow', syncAll);
})();

document.addEventListener('DOMContentLoaded', function () {
    let accordianButtons = document.querySelectorAll('.accordian_button');

    accordianButtons.forEach((button) => {
        button.addEventListener('click', () => {
            const description = button.nextElementSibling;
            document.querySelectorAll('.accordian-description').forEach((desc) => {
                if (desc !== description) {
                    desc.classList.remove('active');
                }
            });

            if (description) {
                description.classList.toggle('active');
            }
        });
    });

    /**
     * Product gallery thumbnail arrows.
     *
     * Modelled on the reference PDP's `SelectionCarouselComponent`: an arrow
     * advances the *selection* by exactly one image (swapping the main image),
     * and the strip only shifts when the newly selected thumbnail would fall
     * outside the visible window. It does not page the whole strip, and the
     * arrows disable at the first/last image rather than at the scroll extents.
     */
    document.querySelectorAll('.product-gallery__thumbnail-wrapper--slider').forEach((wrapper) => {
        const scroller = wrapper.querySelector('.scroller__inner');
        const list = wrapper.querySelector('.product-gallery__thumbnail-list');
        const prevBtn = wrapper.querySelector('[data-direction="prev"]');
        const nextBtn = wrapper.querySelector('[data-direction="next"]');
        if (!scroller || !list || !prevBtn || !nextBtn) return;

        // Media filtered out by the current variant is display:none, so it must
        // not count towards positions or the arrow disabled state.
        const visibleThumbnails = () => Array.prototype.filter.call(
            list.querySelectorAll('.product-gallery__thumbnail'),
            (thumbnail) => !thumbnail.classList.contains('is-filtered')
        );

        // theme.js owns `is-nav-selected`, so it stays the source of truth no
        // matter how the image changed (arrow, thumbnail, swipe, variant swap).
        const selectedIndex = (thumbnails) => {
            const index = thumbnails.findIndex((thumbnail) => thumbnail.classList.contains('is-nav-selected'));
            return index === -1 ? 0 : index;
        };

        // Measured against the viewport rather than offsetLeft, because the
        // thumbnail list is only a positioned offsetParent above 1000px.
        const revealThumbnail = (thumbnail) => {
            if (!thumbnail) return;
            const thumbnailBox = thumbnail.getBoundingClientRect();
            const viewBox = scroller.getBoundingClientRect();

            if (thumbnailBox.left < viewBox.left) {
                scroller.scrollBy({ left: thumbnailBox.left - viewBox.left, behavior: 'smooth' });
            } else if (thumbnailBox.right > viewBox.right) {
                scroller.scrollBy({ left: thumbnailBox.right - viewBox.right, behavior: 'smooth' });
            }
        };

        const updateArrowState = () => {
            const thumbnails = visibleThumbnails();
            const index = selectedIndex(thumbnails);
            prevBtn.classList.toggle('is-disabled', index <= 0);
            nextBtn.classList.toggle('is-disabled', index >= thumbnails.length - 1);
            const hasMultiple = thumbnails.length > 1;
            prevBtn.style.display = hasMultiple ? '' : 'none';
            nextBtn.style.display = hasMultiple ? '' : 'none';
        };

        const selectByOffset = (offset) => {
            const thumbnails = visibleThumbnails();
            const target = thumbnails[selectedIndex(thumbnails) + offset];
            if (!target) return;

            // theme.js delegates on click, so this swaps the main image and
            // moves `is-nav-selected` for us.
            target.click();
            revealThumbnail(target);
        };

        prevBtn.addEventListener('click', () => selectByOffset(-1));
        nextBtn.addEventListener('click', () => selectByOffset(1));
        window.addEventListener('resize', updateArrowState);

        // Catches every selection change, including ones we did not trigger.
        new MutationObserver(updateArrowState).observe(list, {
            subtree: true,
            attributes: true,
            attributeFilter: ['class']
        });

        updateArrowState();
    });
});


/* ==========================================================================
   Accessibility audit fixes
   ========================================================================== */

/**
 * A11y #3 (WCAG 2.4.3 / 4.1.2) — keep `inert` in sync with `aria-hidden`.
 *
 * The theme closes its nav panels by setting aria-hidden="true" and moving them
 * off-screen with CSS. Their links and buttons stay in the tab order (323 of
 * them in the mobile menu alone), so keyboard users tab into invisible content
 * while screen readers are told it does not exist.
 *
 * `inert` removes a subtree from the tab order AND the accessibility tree, so
 * mirroring it onto aria-hidden fixes both halves at once. This lives here
 * rather than in theme.js so a theme update cannot silently revert it.
 */
(function () {
  'use strict';

  var PANEL_SELECTOR = '#mobile-menu, #mini-cart, .mini-cart, .mega-menu, .nav-dropdown';

  function syncInert(panel) {
    var shouldBeInert = panel.getAttribute('aria-hidden') === 'true';

    if (panel.inert === shouldBeInert) {
      return;
    }

    // Never strand focus inside a subtree we are about to disable.
    if (shouldBeInert && panel.contains(document.activeElement)) {
      document.activeElement.blur();
    }

    panel.inert = shouldBeInert;
  }

  function syncTree(root) {
    Array.prototype.forEach.call(root.querySelectorAll(PANEL_SELECTOR), syncInert);
  }

  function init() {
    if (!('inert' in HTMLElement.prototype)) {
      return; // Pre-2023 browser: leave the DOM exactly as the theme built it.
    }

    syncTree(document);

    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'attributes') {
          if (mutation.target.matches && mutation.target.matches(PANEL_SELECTOR)) {
            syncInert(mutation.target);
          }
          return;
        }

        Array.prototype.forEach.call(mutation.addedNodes, function (node) {
          if (node.nodeType !== Node.ELEMENT_NODE) {
            return;
          }
          if (node.matches(PANEL_SELECTOR)) {
            syncInert(node);
          }
          syncTree(node);
        });
      });
    }).observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-hidden']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/**
 * A11y #11 (WCAG 2.4.1) — keep "Skip to content" as the first tab stop.
 *
 * The skip link is already the first element in the theme's markup, but the
 * Swym wishlist app injects its launcher ahead of it at runtime. Re-assert the
 * position after the apps have had their turn.
 */
(function () {
  'use strict';

  function hoistSkipLink() {
    var skipLink = document.querySelector('.skip-to-content');

    if (skipLink && document.body.firstElementChild !== skipLink) {
      document.body.insertBefore(skipLink, document.body.firstChild);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hoistSkipLink);
  } else {
    hoistSkipLink();
  }

  window.addEventListener('load', hoistSkipLink);
})();

/* -------------------------------------------------------------------------- */
/*  Products by keyword (product page)                                        */
/* -------------------------------------------------------------------------- */

/**
 * Fills the "Products by keyword" section (sections/keyword-products.liquid).
 *
 * Liquid cannot search the catalog from a product page, so the section renders
 * an empty placeholder and the product list is fetched from the search URL with
 * the Section Rendering API — the same trick the theme uses for the recently
 * viewed products. The query is built by the section from the first word of the
 * product title, i.e. "Waterfall" for "Waterfall Nightstand".
 *
 * Once the list is in place the section is handed over to the theme by renaming
 * it to "featured-collection" and firing shopify:section:load, which gives it
 * the carousel, the colour swatches and the quick view for free.
 */
(function () {
  'use strict';

  var SECTION_SELECTOR = '[data-section-type="keyword-products"]';

  /**
   * Reads the handle out of a product URL, ignoring the collection prefix and
   * the variant query string: /collections/beds/products/waterfall-bed?variant=1
   */
  function handleFromUrl(url) {
    var parts = (url || '').split('/products/');

    if (parts.length < 2) {
      return '';
    }

    return parts[1].split(/[?#]/)[0].split('/')[0];
  }

  /**
   * The product being viewed is part of its own search results, and the section
   * asks for one extra product to compensate for it.
   */
  function pruneItems(placeholder, currentHandle, productsCount) {
    var items = Array.prototype.slice.call(placeholder.querySelectorAll('.product-item'));

    items.forEach(function (item) {
      var link = item.querySelector('a[href*="/products/"]');

      if (link && handleFromUrl(link.getAttribute('href')) === currentHandle) {
        item.parentNode.removeChild(item);
      }
    });

    items = Array.prototype.slice.call(placeholder.querySelectorAll('.product-item'));

    var limit = typeof productsCount === 'number' && productsCount > 0 ? productsCount : items.length;

    items.slice(limit).forEach(function (item) {
      item.parentNode.removeChild(item);
    });
  }

  function activateSection(section) {
    section.setAttribute('data-section-type', 'featured-collection');

    var wrapper = section.closest('.shopify-section') || section.parentNode;

    wrapper.dispatchEvent(
      new CustomEvent('shopify:section:load', {
        bubbles: true,
        detail: { sectionId: section.getAttribute('data-section-id') }
      })
    );
  }

  function loadSection(section) {
    if (section.hasAttribute('data-keyword-products-loaded')) {
      return;
    }

    section.setAttribute('data-keyword-products-loaded', '');

    var settings;

    try {
      settings = JSON.parse(section.getAttribute('data-section-settings') || '{}');
    } catch (error) {
      return;
    }

    if (!settings.keyword || !settings.searchQuery || !window.routes || !window.routes.searchUrl) {
      return;
    }

    var url =
      window.routes.searchUrl +
      '?section_id=' +
      encodeURIComponent(section.getAttribute('data-section-id')) +
      '&type=product&q=' +
      encodeURIComponent(settings.searchQuery);

    fetch(url, { credentials: 'same-origin', method: 'GET' })
      .then(function (response) {
        return response.text();
      })
      .then(function (content) {
        var temporaryElement = document.createElement('div');
        temporaryElement.innerHTML = content;

        var fetchedPlaceholder = temporaryElement.querySelector('.keyword-products__placeholder');
        var placeholder = section.querySelector('.keyword-products__placeholder');

        if (!fetchedPlaceholder || !placeholder) {
          return;
        }

        placeholder.innerHTML = fetchedPlaceholder.innerHTML;
        pruneItems(placeholder, settings.currentProductHandle, settings.productsCount);

        if (!placeholder.querySelector('.product-item')) {
          placeholder.innerHTML = '';
          return;
        }

        if (section.parentNode) {
          section.parentNode.style.display = 'block';
        }

        activateSection(section);
      })
      .catch(function () {
        /* Leave the section hidden if the products could not be fetched */
      });
  }

  function init(root) {
    (root || document).querySelectorAll(SECTION_SELECTOR).forEach(function (section) {
      loadSection(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  /* Re-fill the section after the theme editor has re-rendered it */
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector) {
      init(event.target);
    }
  });
})();


/* -------------------------------------------------------------------------- */
/*  Collection "Load more" pagination                                          */
/* -------------------------------------------------------------------------- */
/**
 * Shopify slices a collection into pages BEFORE the 'product-hide-check' snippet runs, so a page of
 * `items_per_page` products can render only a handful of cards while the numbered pagination still
 * advertises the un-filtered page count. This module hides that pagination, pulls in the following
 * pages over the Section Rendering API until the grid holds a full page worth of visible products,
 * and offers a "Load more" button for the rest.
 *
 * The server-side paginate tag is left untouched on purpose: CollectionSection in theme.js still
 * owns filtering and sorting and re-renders .collection__dynamic-part on every change, which this
 * module observes so it can re-initialise against the new markup.
 *
 * Known limitation: paginate.items, collection.products_count and the filter counts come from
 * Shopify and still include hidden products. Only removing those products from the Online Store
 * sales channel corrects those numbers.
 */
(function () {
  var SECTION_SELECTOR = '[data-section-type="collection"]';
  var DYNAMIC_PART_SELECTOR = '.collection__dynamic-part';
  var GRID_SELECTOR = '.product-list--collection';
  var PAGINATION_DATA_SELECTOR = '[data-collection-pagination]';
  var ACTIVE_ATTRIBUTE = 'data-collection-load-more-active';
  var READY_ATTRIBUTE = 'data-collection-load-more-ready';

  /* How many products a single top-up tries to add. This is a floor, not a ceiling: whole pages are
     appended, so on a healthy collection one fetch satisfies it outright. On a heavily hidden one it
     keeps the request count sane while still filling a couple of rows. */
  var BATCH_TARGET = 12;

  /* Hidden products cluster, so several consecutive pages can come back completely empty. These caps
     bound how far a single top-up will walk looking for survivors before giving up and handing over
     to the button. */
  var MAX_AUTO_FETCHES = 6;
  var MAX_CLICK_FETCHES = 8;

  function parseJson(element) {
    if (!element) {
      return null;
    }

    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      return null;
    }
  }

  /**
   * Mirrors ProductItemColorSwatch.recalculateSwatches() from theme.js. That instance belongs to
   * CollectionSection and is not reachable from here, and it only runs on its own reloads, so the
   * "view more" swatch indicator has to be recomputed for the cards we append ourselves.
   */
  function recalculateSwatches(root) {
    root.querySelectorAll('.product-item__swatch-list').forEach(function (swatchList) {
      var maxAllowedWidth = Math.min(swatchList.clientWidth, 200);
      var maxFit = Math.floor(maxAllowedWidth / 30);
      var colorSwatches = swatchList.querySelectorAll('.color-swatch');

      colorSwatches.forEach(function (colorSwatch, index) {
        colorSwatch.classList.remove('color-swatch--view-more');

        if (maxFit === index + 1 && maxFit !== colorSwatches.length) {
          colorSwatch.classList.add('color-swatch--view-more');
        }
      });
    });
  }

  function CollectionLoadMore(section) {
    this.section = section;
    this.sectionId = section.getAttribute('data-section-id');
    this.dynamicPart = section.querySelector(DYNAMIC_PART_SELECTOR);

    try {
      this.labels = JSON.parse(section.getAttribute('data-section-settings') || '{}');
    } catch (error) {
      this.labels = {};
    }

    if (!this.dynamicPart || !this.sectionId) {
      return;
    }

    this.inFlight = false;
    this.hasInteracted = false;

    /* Bumped on every refresh so in-flight requests started against an older set of filters can
       tell that their results are no longer wanted. */
    this.generation = 0;

    this._buildButton();
    this._watchForReloads();
    this.refresh();
  }

  /**
   * Re-read the pagination payload and top the grid up. Called on first load and again every time
   * the theme swaps in new markup after a filter, sort or page-size change.
   */
  CollectionLoadMore.prototype.refresh = function () {
    var data = parseJson(this.dynamicPart.querySelector(PAGINATION_DATA_SELECTOR));

    this.grid = this.dynamicPart.querySelector(GRID_SELECTOR);
    this.hasInteracted = false;
    this.inFlight = false;
    this.generation += 1;

    /* No payload means this is not a collection page we handle (the search page reuses the same
       section type), and no grid means the current filters matched nothing. */
    if (!data || !this.grid) {
      this.pages = 0;
      this.nextPage = 1;
      this._renderButton();
      return;
    }

    this.pages = data.pages || 1;
    this.pageSize = data.pageSize || 0;
    this.nextPage = (data.currentPage || 1) + 1;

    /* Take over from the numbered pagination only once we know there is something to take over. */
    this.section.setAttribute(ACTIVE_ATTRIBUTE, '');

    /* Only top up when Shopify's slice left the grid short. Aim for a full first screen rather than
       a full page: chasing all `pageSize` products through a mostly hidden collection costs far more
       requests than it is worth, and the button covers the rest. */
    var target = Math.min(this.pageSize, BATCH_TARGET) - this._visibleCount();

    if (target > 0 && this._hasMore()) {
      this._fill(target, MAX_AUTO_FETCHES);
    } else {
      this._renderButton();
    }
  };

  CollectionLoadMore.prototype._visibleCount = function () {
    return this.grid ? this.grid.querySelectorAll('.product-item').length : 0;
  };

  CollectionLoadMore.prototype._hasMore = function () {
    return this.nextPage <= this.pages;
  };

  CollectionLoadMore.prototype._buildButton = function () {
    this.container = document.createElement('div');
    this.container.className = 'collection__load-more';
    this.container.hidden = true;

    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = 'collection__load-more-button button button--primary';
    this.button.textContent = this.labels.loadMoreLabel || 'Load more';

    this.message = document.createElement('p');
    this.message.className = 'collection__load-more-count';
    this.message.hidden = true;

    this.container.appendChild(this.button);
    this.container.appendChild(this.message);

    /* Deliberately a sibling of .collection__dynamic-part rather than a child: the theme replaces
       that element's innerHTML wholesale on every filter change, which would both destroy the
       button and retrigger our own MutationObserver. */
    this.dynamicPart.parentNode.insertBefore(this.container, this.dynamicPart.nextSibling);

    this.button.addEventListener('click', this._onClick.bind(this));
  };

  CollectionLoadMore.prototype._renderButton = function () {
    if (!this.container) {
      return;
    }

    var hasMore = Boolean(this._hasMore() && this.grid);

    this.container.hidden = !hasMore && !this.hasInteracted;
    this.button.hidden = !hasMore;
    this.button.setAttribute('aria-busy', this.inFlight ? 'true' : 'false');
    this.button.textContent = this.inFlight
      ? this.labels.loadingLabel || 'Loading...'
      : this.labels.loadMoreLabel || 'Load more';

    /* Only worth saying once the shopper has actually asked for more. */
    var isExhausted = !hasMore && this.hasInteracted && !this.inFlight;

    this.message.hidden = !isExhausted;
    this.message.textContent = isExhausted ? this.labels.allLoadedLabel || '' : '';
  };

  CollectionLoadMore.prototype._onClick = function () {
    if (this.inFlight || !this._hasMore()) {
      return;
    }

    this.hasInteracted = true;
    this._fill(Math.min(this.pageSize, BATCH_TARGET), MAX_CLICK_FETCHES);
  };

  CollectionLoadMore.prototype._pageUrl = function (page) {
    var url = new URL(window.location.href);

    url.searchParams.set('page', String(page));
    url.searchParams.set('section_id', this.sectionId);

    return url.toString();
  };

  CollectionLoadMore.prototype._fetchPage = function (page) {
    return fetch(this._pageUrl(page), { credentials: 'same-origin', method: 'GET' })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Unable to load collection page ' + page);
        }

        return response.text();
      })
      .then(function (content) {
        var temporaryElement = document.createElement('div');
        temporaryElement.innerHTML = content;

        var fetchedGrid = temporaryElement.querySelector(GRID_SELECTOR);

        if (!fetchedGrid) {
          return [];
        }

        /* Direct children only: the hide check has already run server-side, so whatever came back
           is exactly the set of products that should be visible on that page. */
        return Array.prototype.filter.call(fetchedGrid.children, function (child) {
          return child.classList.contains('product-item');
        });
      });
  };

  /**
   * Walk forward through the remaining pages, appending the cards that survived the hide check,
   * until `wanted` products have been added, the pages run out, or `maxFetches` is reached.
   */
  CollectionLoadMore.prototype._fill = function (wanted, maxFetches) {
    var self = this;
    var generation = this.generation;
    var grid = this.grid;
    var added = 0;
    var fetches = 0;

    this.inFlight = true;
    grid.classList.add('is-loading-more');
    this._renderButton();

    function isStale() {
      return self.generation !== generation;
    }

    function step() {
      if (isStale() || added >= wanted || !self._hasMore() || fetches >= maxFetches) {
        return Promise.resolve();
      }

      var page = self.nextPage;

      self.nextPage += 1;
      fetches += 1;

      return self._fetchPage(page).then(function (items) {
        /* The shopper changed a filter while this was in flight, so these cards belong to a
           collection view that is no longer on screen. Drop them. */
        if (isStale()) {
          return Promise.resolve();
        }

        if (items.length) {
          items.forEach(function (item) {
            grid.appendChild(item);
          });

          added += items.length;
          recalculateSwatches(grid);
        }

        return step();
      });
    }

    return step()
      .catch(function () {
        /* Leave whatever loaded in place and let the shopper retry with the button */
      })
      .then(function () {
        grid.classList.remove('is-loading-more');

        /* A newer refresh already owns inFlight and the button state */
        if (isStale()) {
          return;
        }

        self.inFlight = false;
        self._renderButton();
      });
  };

  /**
   * CollectionSection replaces .collection__dynamic-part's innerHTML after every filter, sort or
   * page-size change. Watching its direct children tells us when to re-read the payload. Our own
   * appends go into .product-list--collection, a level deeper, so they never retrigger this.
   */
  CollectionLoadMore.prototype._watchForReloads = function () {
    var self = this;

    if (!window.MutationObserver) {
      return;
    }

    this.observer = new MutationObserver(function () {
      self.refresh();
    });

    this.observer.observe(this.dynamicPart, { childList: true });
  };

  function init(root) {
    (root || document).querySelectorAll(SECTION_SELECTOR).forEach(function (section) {
      if (section.hasAttribute(READY_ATTRIBUTE)) {
        return;
      }

      if (!section.querySelector(PAGINATION_DATA_SELECTOR)) {
        return;
      }

      section.setAttribute(READY_ATTRIBUTE, '');
      new CollectionLoadMore(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  /* Re-initialise after the theme editor has re-rendered the section */
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector) {
      init(event.target);
    }
  });
})();
